import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';

/**
 * Toggles the "design & development by SoloMediaGroup" credits banner
 * revealed along the bottom edge of the footer by the "Site Credit" button.
 * The bar stays `hidden` (removed from the accessibility tree and tab order)
 * except during the open/closing animation, when it's briefly visible but
 * mid-transition.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} toggle
 * @property {HTMLDivElement} bar
 *
 * @extends {Component<Refs>}
 */
class FooterSiteCredits extends Component {
  requiredRefs = ['toggle', 'bar'];

  toggle = () => {
    if (this.refs.bar.hidden) {
      this.#open();
    } else {
      this.#close();
    }
  };

  #open() {
    const { toggle, bar } = this.refs;

    bar.hidden = false;
    bar.classList.remove('at-footer-credits__bar--closing');
    // Force a reflow so the enter animation reliably plays from its `from` state.
    void bar.offsetWidth;
    bar.classList.add('at-footer-credits__bar--open');
    toggle.setAttribute('aria-expanded', 'true');
  }

  #close = async () => {
    const { toggle, bar } = this.refs;

    bar.classList.remove('at-footer-credits__bar--open');
    bar.classList.add('at-footer-credits__bar--closing');
    toggle.setAttribute('aria-expanded', 'false');

    await onAnimationEnd(bar, undefined, { subtree: false });

    bar.classList.remove('at-footer-credits__bar--closing');
    bar.hidden = true;
  };
}

if (!customElements.get('at-footer-site-credits')) {
  customElements.define('at-footer-site-credits', FooterSiteCredits);
}
